import { useEffect, useMemo, useState, useTransition } from "react";
import { useSelector } from "react-redux";
import { HotelsAvailabilitySelectors } from "@b2bportal/core-hotels";
import { HotelSortOptionEnum } from "@b2bportal/core-types";
import type { Lodging } from "@b2bportal/lodging-api";
import { usePerformFilterOnHotelList } from "@tenant/hotels";
import { useHotelListOverride } from "../../context/HotelListOverrideContext";

/**
 * RBC override of useHotelList that supports custom sort comparators
 * and price filtering via HotelListOverrideContext.
 *
 * When overrides are provided (e.g., from PackagesHotelSlotProvider):
 * - sortComparator: re-sorts by custom comparator when sorting by price
 * - priceExtractor: applies price filtering using the extracted price
 *   (core's price filter is disabled via useHotelsAvailabilityFilters override)
 *
 * For standalone hotels (no context), behaves identically to core.
 *
 * Uses a lazy initializer on useState to pre-filter the hotel list on
 * first render, preventing unavailable hotels from briefly flashing.
 */
export const useHotelList = () => {
  const [isHotelListFiltering, startTransition] = useTransition();

  const hotels = useSelector(HotelsAvailabilitySelectors.getHotels);
  const sort = useSelector(HotelsAvailabilitySelectors.getSort);
  const priceFilter = useSelector(HotelsAvailabilitySelectors.getPriceFilter);
  const hasSetPriceFilter = useSelector(
    HotelsAvailabilitySelectors.hasSetPriceFilterSelector
  );
  const { handlers } = usePerformFilterOnHotelList();
  const [hotelList, setHotelList] = useState<Lodging[]>([]);

  const override = useHotelListOverride();

  useEffect(() => {
    startTransition(() => {
      let result =
        hotels.length > 0
          ? hotels.filter((hotel) => handlers.performFilter(hotel))
          : [];

      // Apply package price filter when priceExtractor is provided
      // (core's performPriceFilter is disabled via hasSetPriceFilterSelector = false)
      if (
        override?.priceExtractor &&
        priceFilter &&
        hasSetPriceFilter &&
        result.length > 0
      ) {
        const { priceExtractor } = override;
        result = result.filter((hotel) => {
          const price = priceExtractor(hotel);
          if (price == null) return false;
          return priceFilter.min <= price && priceFilter.max >= price;
        });
      }

      // Apply custom sort comparator when provided and sorting by price
      if (override?.sortComparator && sort === HotelSortOptionEnum.Price) {
        result = [...result].sort(override.sortComparator);
      }

      setHotelList((prev) => {
        if (prev.length !== result.length) return result;

        if (prev.every((val, i) => val.lodging.id === result[i].lodging.id)) {
          return prev;
        }
        return result;
      });
    });
  }, [hotels, handlers, override, sort, priceFilter]);

  return useMemo(
    () => ({
      context: {
        isHotelListFiltering,
        hotelsList: hotelList ?? [],
      },
    }),
    [hotelList, isHotelListFiltering]
  );
};
